import * as React from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Table from "react-bootstrap/Table";
import PaginationButtons from "./PaginationButtons";
import {Helper, OtherFeaturesPanel} from "./OtherFeaturesPanel";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import CategoricalCell from "./CategoricalCell";
import {CategoricalCellDto,ColumnIds,PublisherOrSiteOption,ResponseStatus, DatagridColumn, SortState, PageState, PivotMode, PivotIds, DateModes, SearchResponseDto, TrafficSourceResponseDto, ProductsCampaignsDto, UserInfo} from "../utils/types";
import TimeAgo from 'timeago-react';
import { datagridColumns } from "../utils/datagridCommon";
import "./loading.css";
import { DatagridHelper } from "./DatagridHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import StateHelper from "../utils/StateHelper";
import SummaryRow from "./SummaryRow";
import Chart from "../components/charts/Chart";
import dayjs from "../utils/dayjs";
import { PagesHelper } from "../common/PagesHelper";
import Loading from "../utils/Loding";
import { calculateISO8601WeekBoundaries } from "../utils/DateUtil";

interface DataGridPanelProps {
  searchResponseDto: SearchResponseDto;
  setSearchResponseDto: (searchResponseDto: SearchResponseDto) => void;

  trafficSourceResponseDto: TrafficSourceResponseDto;
  setTrafficSourceResponseDto : (trafficSourceResponseDto: TrafficSourceResponseDto) => void;

  productsCampaignsDto?: ProductsCampaignsDto;
  setProductsCampaignsDto?: (productsCampaignsDto: ProductsCampaignsDto) => void;

  search: (updateHistory?: boolean) => void;
  datagridColumns: Map<string, DatagridColumn>;
  
  page: string;
  actualPage: number;
  setActualPage: (actualPage: number) => void;

  currentUser: UserInfo;
}  

export function DataGridPanel(props: DataGridPanelProps) {
  const [pageSize,setPageSize] = React.useState(null);
  let filteredColumns: DatagridColumn[] = [];
  const [,setRefresh] = React.useState(0);

  function onPivotChange(newPivotFieldSelected:any, newPivotGroupBySelected:any) {
    const pageState:PageState = StateHelper.getPageState();
    const pivotBy = newPivotGroupBySelected.id !== PivotIds.off ? newPivotFieldSelected + ":" + newPivotGroupBySelected.id : null;

    if (pivotBy !== pageState.pivotBy) {
      const isOnPivotSelection = true;
      const { newGroupBy, newDateStart, newDateEnd, newPivotBy, newDateMode } = DatagridHelper.checkPivotCombinationWithGroupsByAndDates(pivotBy, pageState.groupBy, pageState.dateStart, pageState.dateEnd, isOnPivotSelection, pageState.dateMode);
      const haveToSearch = newGroupBy !== pageState.groupBy || !newDateStart.isSame(pageState.dateStart) || !newDateEnd.isSame(pageState.dateEnd) || newDateMode !== pageState.dateMode;

      pageState.pivotBy = newPivotBy;
      pageState.dateStart = newDateStart;
      pageState.dateEnd = newDateEnd;
      pageState.groupBy = newGroupBy;
      pageState.dateMode = newDateMode;
      if (haveToSearch) {
        const searchFunction = StateHelper.getSearchFuntion(pageState.page);
        searchFunction();
      } else {
        PagesHelper.historyPushFromPageState();
        setRefresh(Date.now());
      }
    }
  }

  function setSort(columnIdToSort: string) {
    if (sortState.columnIdSorted === columnIdToSort) {
      if (sortState.sort === 1) {
        sortState.sort = 2;
      } else if(sortState.sort === 2) {
        sortState.sort = 1;
      }
    }
    sortState.columnIdSorted = columnIdToSort;
    setRefresh(Date.now());
  }

  if (props.searchResponseDto === undefined || props.searchResponseDto === null) {
    return  (
      <Row>
        <Col sm={3} className="text-right"></Col>
        <Col sm={6} className="text-right">
          <Alert key="alertNoData1" variant="warning" className="text-center">There are no results for the current filters.</Alert>
        </Col>
        <Col sm={3} className="text-right"></Col>
      </Row>
    );
  }

  if (props.searchResponseDto.status === ResponseStatus.LOADING) {
    return  (
      <Loading />
    );
  }

  if (props.searchResponseDto.status ===  ResponseStatus.ERROR) {
    return (
      <Row>
        <Col sm={3} className="text-right"></Col>
        <Col sm={6} className="text-center">
          <Alert
            title={"Reporting data updated: " + dayjs.utc().format("YYYY-MM-DD hh:mm:ss") + " (local time)"}
            key="alertNoData1"
            variant="danger"
            className="text-center border border-danger"
          >
            <div>{props.searchResponseDto?.error ? props.searchResponseDto.error : "Unable to load results from the server."}</div>
            <div>Queried: <TimeAgo  datetime={ dayjs.utc().toDate()}  locale='en_US'/></div>
          </Alert>
          <Button size="sm" variant="outline-primary" onClick={() => props.search()}>Retry</Button>
        </Col>
        <Col sm={3} className="text-right"></Col>
      </Row>
    );
  }  

  filteredColumns = DatagridHelper.filterColumns(props.searchResponseDto.metadata.columns, props.datagridColumns, props.page);
  
  let trs: any[] = [];
  let rowsResponse = props.searchResponseDto.data;
  let rows:any[] = rowsResponse;
  let fields = filteredColumns.filter((c:DatagridColumn) => datagridColumns.get(c.id).isGroupByColumn === false);
  let defaultColumnId = ColumnIds.revenueApptap;
  if (!fields.find((column) => column.id === ColumnIds.revenueApptap)) {
    if (fields.find((column) => column.id === ColumnIds.revenuePublisher)) {
      defaultColumnId = ColumnIds.revenuePublisher;
    } else {
      defaultColumnId = fields[0].id as ColumnIds;
    }
  }

  const pageState:PageState = StateHelper.getPageState();
  const sortState:SortState = StateHelper.getSortState();

  // pivot read
  let { pivotFieldSelected, pivotGroupBySelected } = DatagridHelper.readPivot(pageState.pivotBy);
  const isPivotFieldSelectedOnFields = fields.find((f => f.id === pivotFieldSelected));
  if (!isPivotFieldSelectedOnFields && pivotFieldSelected !== "All") {
    pivotFieldSelected = datagridColumns.get(defaultColumnId).id;
  }

  let tmpPageSize : number = 25;
  if (pageSize === null) {
    //page size has not been initialized.
    let storedPageSize = window.localStorage.getItem("apptap.dashboard.pageSize");
    if ( storedPageSize && ! Number.isNaN(storedPageSize) ){
      setPageSize(storedPageSize);
      tmpPageSize = Number.parseInt(storedPageSize); 
    }
    else{
      setPageSize(25);
      tmpPageSize = 25; 
    }
  }
  else{
    tmpPageSize = pageSize; 
  }

  const publishersArray : PublisherOrSiteOption[] = props.trafficSourceResponseDto && props.trafficSourceResponseDto.publishersArray 
                                                      ? props.trafficSourceResponseDto.publishersArray : [];

  const productsResponse = props.productsCampaignsDto && props.productsCampaignsDto.productsResponse 
                            ?  props.productsCampaignsDto.productsResponse : [];
  const campaignsResponse = props.productsCampaignsDto && props.productsCampaignsDto.campaignsResponse 
                            ?  props.productsCampaignsDto.campaignsResponse : [];

  let summaryRow : Record<string,number> = {};
  const summaryRowForPivotByGroup: any = {};

  const columnHeaders : any[] = [];

  // calculated values
  rowsResponse.forEach((row:Record<string, (number|string)> ) => {
    filteredColumns.forEach((column) => {

      if (row[column.id] === undefined) {
        const cellValue = DatagridHelper.calculateColumnValue(row, column, publishersArray);
        row[column.id] = cellValue;
      }
    });
  });

  let mode = PivotMode.NO_PIVOT;
  let filteredColumnsFromResponse:any[] = filteredColumns;

  const groupsBy = pageState.groupBy ? pageState.groupBy.split(",") : [];
  const groupsByFiltered = groupsBy.filter((groupBy:string) => groupBy !== ColumnIds.date && groupBy !== ColumnIds.month && groupBy !== ColumnIds.week);
  const isGroupingOnlyByDateOrMonth = groupsByFiltered.length === 0;

  // pivot transposing
  if (pivotGroupBySelected && pivotGroupBySelected.id !== PivotIds.off && pivotFieldSelected) {
    const newColumns : DatagridColumn[] = [];
    const newRows:any = [];
    let dateMode: DateModes = null;
    let dateAbrv: string;
    if ( groupsBy.includes(ColumnIds.date) ) {
      dateMode = "days";
      dateAbrv = "d";
    } else if ( groupsBy.includes(ColumnIds.week) ) {
      dateMode = "weeks";
      dateAbrv = "w";
    } else if ( groupsBy.includes(ColumnIds.month) ) {
      dateMode = "months";
      dateAbrv = "m";
    }
    
    if ( dateMode != null ) {
      const datesFiltered = DatagridHelper.getDatesOrMonthsInRange(pageState.dateStart, pageState.dateEnd, dateMode);
      if (isGroupingOnlyByDateOrMonth || pivotFieldSelected === "All") {

        mode = PivotMode.PIVOT_ALL_FIELDS;
        const publisherColumnConfig = datagridColumns.get(ColumnIds.apptapPublisherID);
        newColumns.push({
          ...publisherColumnConfig,
          id: "fields" as ColumnIds,
          name: "	",
          isGroupByColumn: true,
          showQuickFilterMenu: false
        });
        const dateColumnConfig = datagridColumns.get(ColumnIds.date);

        newColumns.push({
          ...dateColumnConfig,
          id: ColumnIds.avgColumn,
          name: Object.values(datesFiltered).length + dateAbrv + " Avg.",
          isGroupByColumn: true,
          showQuickFilterMenu: false,
          align: "right",
          precision: 0,
          style: { minWidth: 70, whiteSpace: "nowrap" }
        });

        Object.values(datesFiltered).forEach((dateColumn:any) => {
          newColumns.push({
            ...dateColumnConfig,
            id: dateColumn,
            name: dateColumn,
            showQuickFilterMenu: false,
            align: "right",
            style: { minWidth: 70, whiteSpace: "nowrap" }
          });
          summaryRowForPivotByGroup[dateColumn] = {};
        });
        summaryRowForPivotByGroup[ColumnIds.avgColumn] = 0;

        filteredColumns = newColumns;

        fields.forEach((field) => {
          if (isGroupingOnlyByDateOrMonth && pivotFieldSelected !== "All" && field.id !== pivotFieldSelected) return;
          const newRow:any = {
            fields: field.name,
            style: field.style,
            columnIdForFormat: field.id
          };
          filteredColumns.forEach((column) => {
            const rowsTmp = rowsResponse.filter((rowTmp:any) => {
              if ( dateMode === "days" ) { 
                return rowTmp.date === column.id;
              } else if ( dateMode === "weeks" ) {
                return rowTmp.week === column.id;
              } else if ( dateMode === "months" ) {
                return rowTmp.month === column.id;
              } else {
                return false;
              }
            });
            rowsTmp.forEach((rowTmp:any) => {
              const cellValue = rowTmp[field.id];
              if (!newRow[column.id]) {
                newRow[column.id] = cellValue;
              } else {
                newRow[column.id] += cellValue;
              }
              if (pivotFieldSelected !== "All") {
                if (!summaryRow[column.id]) {
                  summaryRow[column.id] = cellValue;
                } else {
                  summaryRow[column.id] += cellValue;
                }
              }
            });
          });
          newRows.push(newRow);
        });

      } else {
        mode = PivotMode.PIVOT_GROUP_BY;

        const columnOfpivotFieldSelectedConfig = props.datagridColumns.get(pivotFieldSelected);

        newColumns.push({
          ...columnOfpivotFieldSelectedConfig,
          id: ColumnIds.avgColumn,
          name: Object.values(datesFiltered).length + dateAbrv + " Avg.",
          isGroupByColumn: true,
          showQuickFilterMenu: false,
          align: "right",
          style: { minWidth: 70, whiteSpace: "nowrap" }
        });

        Object.values(datesFiltered).forEach((dateColumn:any) => {
          newColumns.push({
            ...columnOfpivotFieldSelectedConfig,
            id: dateColumn,
            name: dateColumn,
            isGroupByColumn: true,
            align: "right",
            style: { minWidth: 70, whiteSpace: "nowrap" }
          });
          summaryRowForPivotByGroup[dateColumn] = {};
        });
        summaryRowForPivotByGroup[ColumnIds.avgColumn] = 0;
        filteredColumns = newColumns;
      }

      // build of summaryRowForPivotByGroup
      if (pivotFieldSelected !== "All") {
        if (groupsByFiltered.includes(ColumnIds.apptapPublisherID)) {
          groupsByFiltered.unshift(ColumnIds.apptapPublisherName);
        }
        groupsByFiltered.forEach((groupBy:string) => {
          const groupByColumnConfig = datagridColumns.get(groupBy);
          newColumns.unshift({
            ...groupByColumnConfig,
            id: groupBy as ColumnIds
          });
        });
  
        rowsResponse.forEach((row:Record<string, (number|string)>) => {
          let rowDateOrMonth: string;
          if ( dateMode === "days" ) {
            rowDateOrMonth = row.date as string;
          } else if ( dateMode === "weeks" ) {
            rowDateOrMonth = row.week as string;
          } else if ( dateMode === "months" ) {
            rowDateOrMonth = row.month as string;
          }
  
          // new row fill
          const newRow = newRows.find((rowTmp:any) => {
            for (let groupBy of groupsByFiltered) {
              if (rowTmp[groupBy] !== row[groupBy]) {
                return false;
              }
            }
            return true;
          });
          let index = 0;
  
          if (newRow) {
            newRow[rowDateOrMonth] = row[pivotFieldSelected];
            index = newRows.indexOf(newRow);
          } else {
            const newRow = { [rowDateOrMonth]: row[pivotFieldSelected] };
            groupsByFiltered.forEach((groupBy:string) => newRow[groupBy] = row[groupBy]);
            newRows.push(newRow);
            index = newRows.length - 1;
          }
  
          // summary of needed columns for calculated columns
          filteredColumnsFromResponse.forEach((column:any) => {
            const cellValue = row[column.id];
            if (cellValue !== null && cellValue !== undefined && typeof(cellValue) != 'string' && !Array.isArray(cellValue) ) {
              if ( !summaryRowForPivotByGroup[rowDateOrMonth] ) {
                console.error("Summary row doesn't have a value for: ", rowDateOrMonth);
                return;
              }

              if (!summaryRowForPivotByGroup[rowDateOrMonth][index]) {
                summaryRowForPivotByGroup[rowDateOrMonth][index] = { index: index };
              }
              if (!summaryRowForPivotByGroup[rowDateOrMonth][index][column.id] ) {
                summaryRowForPivotByGroup[rowDateOrMonth][index][column.id] = cellValue;
              } else {
                summaryRowForPivotByGroup[rowDateOrMonth][index][column.id] += cellValue;
              }
              if (!summaryRowForPivotByGroup[rowDateOrMonth][column.id] ) {
                summaryRowForPivotByGroup[rowDateOrMonth][column.id] = cellValue;
              } else {
                summaryRowForPivotByGroup[rowDateOrMonth][column.id] += cellValue;
              }
            }
          });
        });
      }

      // set rows
      rows = newRows;
    }
  }

  // default sort
  if (!filteredColumns.find((column) => column.id === sortState.columnIdSorted)) {
    sortState.sort = 1;
    if (filteredColumns.find((column) => column.id === ColumnIds.avgColumn)) {
      sortState.columnIdSorted = ColumnIds.avgColumn;
    } else if (pageState.groupBy !== null && pageState.groupBy.includes(ColumnIds.date)) {
      sortState.columnIdSorted = ColumnIds.date;
    } else if (pageState.groupBy !== null && pageState.groupBy.includes(ColumnIds.month)) {
      sortState.columnIdSorted = ColumnIds.month;
    } else {
      sortState.columnIdSorted = ColumnIds.clicksReceived;
    }
  }

  // set invisible columns
  const invisCols = Helper.getInvisibleColumns(pageState.page);
  filteredColumns.forEach((column : any) => column.visible = !invisCols.includes(column.id));

  // keep all columns for export
  const filteredColumnsForExport = filteredColumns;

  // if both campaign source and campaign source alias are included, skip the campaign source alias column (and put both values in campaign source)
  const skipAliasColumn = filteredColumns.filter(c => c.id === ColumnIds.campaignSource || c.id === ColumnIds.campaignSourceAlias).length > 1;
  if ( skipAliasColumn ) {
    filteredColumns = filteredColumns.filter((c) => c.id !== ColumnIds.campaignSourceAlias);
  }
  // if publisherID or publisherID and siteID are present, combine them into the traffic source column as a single combo column
  const skipTrafficSourceIDs = filteredColumns.filter(c => c.id === ColumnIds.apptapPublisherID).length === 1;
  if ( skipTrafficSourceIDs ) {
    filteredColumns = filteredColumns.filter((c) => c.id !== ColumnIds.apptapPublisherID && c.id !== ColumnIds.apptapSiteID);
  }
  // if both cLinkID and owner are included, skip owner column (and put both values in cLinkId)
  const skipOwnerColumn = filteredColumns.filter(c => c.id === ColumnIds.cLinkId || c.id === ColumnIds.cLinkOwner).length > 1;
  if ( skipOwnerColumn ) {
    filteredColumns = filteredColumns.filter((c) => c.id !== ColumnIds.cLinkOwner);
  }

  // track presence of date/month column to know when to add 
  const hasDateColumn  = filteredColumns.filter(c => c.id === ColumnIds.date).length > 0;
  const hasMonthColumn = filteredColumns.filter(c => c.id === ColumnIds.month).length > 0;

  // calculate summary row
  switch (mode) {
    case PivotMode.NO_PIVOT:
      summaryRow = DatagridHelper.calculateSummary(rows, filteredColumns, publishersArray);
      break;
    case PivotMode.PIVOT_ALL_FIELDS:
      summaryRow = DatagridHelper.calculateSummary(rowsResponse, filteredColumnsFromResponse, publishersArray);

      // calculate avg column
      rows.forEach((row:Record<string, (number|string)>) => {
        const columnId = row["columnIdForFormat"].toString();
        const column = datagridColumns.get(columnId);
        const summaryValue = summaryRow[columnId];
        row[ColumnIds.avgColumn] = DatagridHelper.calculateAverage(summaryValue, row, column);
      });
      break;

    case PivotMode.PIVOT_GROUP_BY:
      filteredColumns.forEach((column) => {
        if (!column.visible) return;
        
        if (summaryRowForPivotByGroup[column.id]) {
          const cellValue = DatagridHelper.calculateColumnValue(summaryRowForPivotByGroup[column.id], props.datagridColumns.get(pivotFieldSelected), publishersArray);
          if ( typeof(cellValue) !== 'string' ) {
            summaryRow[column.id] = cellValue;
          }
        }
      });

      // calculate avg column
      let index = 0;
      rows.forEach((row:Record<string, (number|string)>) => {
        const tmpSum: any = {};
        filteredColumns.forEach((column) => {
          if (column.type === "string" || column.id === ColumnIds.avgColumn) return;

            filteredColumnsFromResponse.forEach((columnFromResponse) => {
            const cellValue = summaryRowForPivotByGroup[column.id][index]?.[columnFromResponse.id];
            if ( cellValue !== null && cellValue !== undefined && typeof(cellValue) != 'string' && !Array.isArray(cellValue) ) {
              if (!tmpSum[columnFromResponse.id]) {
                tmpSum[columnFromResponse.id] = cellValue;
              } else {
                tmpSum[columnFromResponse.id] += cellValue;
              }
            }
          });
        });
        const cellValue = DatagridHelper.calculateColumnValue(tmpSum, props.datagridColumns.get(pivotFieldSelected), publishersArray);
        row[ColumnIds.avgColumn] = DatagridHelper.calculateAverage(cellValue, row, props.datagridColumns.get(pivotFieldSelected));

        index++;
      });

      // calculate summary of avg column
      Object.keys(summaryRowForPivotByGroup).forEach((columnDate:any) => {
        filteredColumnsFromResponse.forEach((column) => {
          if (column.id === ColumnIds.date ||column.id === ColumnIds.month || column.type === "strings") return;
          if (summaryRowForPivotByGroup[columnDate][column.id]) {
            const cellValue = DatagridHelper.calculateColumnValue(summaryRowForPivotByGroup[columnDate], props.datagridColumns.get(column.id), publishersArray);
            if ( typeof(cellValue) !== 'string' ) {
              if (!summaryRow[column.id]) {
                summaryRow[column.id] = cellValue;
              } else {
                summaryRow[column.id] += cellValue;
              }
            }
          }
        });

        const summaryValue = DatagridHelper.calculateColumnValue(summaryRow, props.datagridColumns.get(pivotFieldSelected), publishersArray);
        summaryRow[ColumnIds.avgColumn] = DatagridHelper.calculateAverage(summaryValue, summaryRow, props.datagridColumns.get(pivotFieldSelected));
      });
      break;
  }

  // sorted rows
  if (pivotFieldSelected !== "All") {
    rows = DatagridHelper.sortColumn(rows);
  }

  filteredColumns.forEach((column, columnIndex) => {
    if (!column.visible) return;

    // header row and sort
    let icon = <FontAwesomeIcon icon={faSort} />;
    let color: string = "#ccc";

    if (column.id === sortState.columnIdSorted) {
      switch (sortState.sort) {
        case 1: // descending
          icon = <FontAwesomeIcon icon={faSortDown} />;
          color = "#000";
          break;
        case 2: // ascending
          icon = <FontAwesomeIcon icon={faSortUp} />;
          color = "#000";
          break;
      }
    }

    let tdHeaderObj = (
      <th key={1000 + columnIndex} style={{...column.style, cursor: mode !== PivotMode.PIVOT_ALL_FIELDS ? "pointer" : "default" }} align="center" title={column.name} onClick={() => mode !== PivotMode.PIVOT_ALL_FIELDS && setSort(column.id)}>
        <b>{column.name}</b>
        { 
          mode !== PivotMode.PIVOT_ALL_FIELDS && <span style={{"marginLeft": "5px", color: color, fontSize: "120%"}}>{icon}</span>
        }
      </th>
    );
    columnHeaders.push(tdHeaderObj);
  });

  let lastIndex = props.actualPage * tmpPageSize;
  let initialIndex = lastIndex - tmpPageSize;
  lastIndex = lastIndex > rows.length  ? rows.length : lastIndex;

  // generate trs and tds
  rows.forEach((row:Record<string, (number|string)>, rowIndex) => {
    const tds: React.ComponentPropsWithoutRef<'td'>[] = [];

    filteredColumns.forEach((column, columnIndex) => {
      if (rowIndex >= initialIndex && rowIndex < lastIndex) {
        const cellValue = row[column.id];
        const columnForFormat = mode === PivotMode.PIVOT_ALL_FIELDS && column.id !== "fields"? datagridColumns.get(row["columnIdForFormat"].toString()) : column;
        let formattedValue = DatagridHelper.formatValue(columnForFormat.type, cellValue, columnForFormat.precision);
        let tooltipFormattedValue = DatagridHelper.formatValue(columnForFormat.type, cellValue, columnForFormat.precision, true);

        if ( skipAliasColumn && column.id === ColumnIds.campaignSource ) {
            const sourceValue = DatagridHelper.formatValue(column.type, cellValue, column.precision);
            const aliasValue = DatagridHelper.formatValue(datagridColumns.get(ColumnIds.campaignSourceAlias).type, row[ColumnIds.campaignSourceAlias], datagridColumns.get(ColumnIds.campaignSourceAlias).precision);
            formattedValue = (<div>
                                <span>{sourceValue}</span>
                                <br/>
                                <i>{aliasValue}</i>
                              </div>);
    
            tooltipFormattedValue = sourceValue + " (alias: " + aliasValue + ")";
        }
  
        // combine the values of publisher name, publisher ID, and site ID into a single display field
        if ( skipTrafficSourceIDs && column.id === ColumnIds.apptapPublisherName ) {
          const sourceName = DatagridHelper.formatValue(column.type, cellValue, column.precision);
          const pubIDValue = datagridColumns.get(ColumnIds.apptapPublisherID) && DatagridHelper.formatValue(datagridColumns.get(ColumnIds.apptapPublisherID).type, row[ColumnIds.apptapPublisherID], datagridColumns.get(ColumnIds.apptapPublisherID).precision);
          const siteIDValue = datagridColumns.get(ColumnIds.apptapSiteID) && DatagridHelper.formatValue(datagridColumns.get(ColumnIds.apptapSiteID).type, row[ColumnIds.apptapSiteID], datagridColumns.get(ColumnIds.apptapSiteID).precision);
  
          const trafficSourceID = (pubIDValue && siteIDValue) ? (pubIDValue + ":" + siteIDValue) : pubIDValue;
  
          formattedValue = (<div>
                              <span>{sourceName}</span>
                              <br/>
                              <i>{trafficSourceID}</i>
                            </div>);
  
          tooltipFormattedValue = sourceName + " (" + trafficSourceID + ")";
        }

        // combine the values of cLinkID and owner into a single display field
        if ( skipOwnerColumn && column.id === ColumnIds.cLinkId ) {
          const cLinkValue = DatagridHelper.formatValue(column.type, cellValue, column.precision);
          const ownerValue = DatagridHelper.formatValue(datagridColumns.get(ColumnIds.cLinkOwner).type, row[ColumnIds.cLinkOwner], datagridColumns.get(ColumnIds.cLinkOwner).precision);

          formattedValue = (<div>
                              <span>{cLinkValue}</span>
                              <br/>
                              <i>{ownerValue}</i>
                            </div>);

          tooltipFormattedValue = cLinkValue + " (owner: " + ownerValue + ")";
        }

        // if ISO8601 week is showing, we want to show the date ranges that
        // it covers - for all but the case when "date" is also showing, because
        // then every row is a single day - a week and month don't usually line up
        // so "month" will split each iso8601 week into multiple weeks
        if ( column.id === ColumnIds.week && !hasDateColumn ) {
          const weekValue = row[ColumnIds.week] as string;
          const monthValue = hasMonthColumn ? row[ColumnIds.month] as string : "";
          
          const [weekStart, weekEnd] = calculateISO8601WeekBoundaries(weekValue, monthValue);

          formattedValue = (<div>
                              <span>{weekValue}</span>
                              <br/>
                              <i>{weekStart.format("YYYY-MM-DD")} - {weekEnd.format("YYYY-MM-DD")}</i>
                            </div>);

          tooltipFormattedValue = weekValue + " (" + weekStart.format("YYYY-MM-DD") + " - " + weekEnd.format("YYYY-MM-DD") + ")";
        }

        const tdKey = "r" + rowIndex.toString() + "c" + columnIndex.toString();
        if (column.visible === true) {

          let tdObj = null;
          if (column.showQuickFilterMenu === true) {

            const categoricalCellDto : CategoricalCellDto = {
              page : props.page,
              columnId : column.id,
              apptapPlacementId : row[ColumnIds.apptapPlacementID] ? row[ColumnIds.apptapPlacementID].toString() : null,
              publisherPlacementId : row[ColumnIds.publisherPlacementID] ? row[ColumnIds.publisherPlacementID].toString() : null,
              siteId : row[ColumnIds.apptapSiteID] ? row[ColumnIds.apptapSiteID].toString() : null,
              publisherId : row[ColumnIds.apptapPublisherID] ? row[ColumnIds.apptapPublisherID].toString() : null,
              publisherName : row[ColumnIds.apptapPublisherName] ? row[ColumnIds.apptapPublisherName].toString() : null,
              campaignSource : row[ColumnIds.campaignSource] ? row[ColumnIds.campaignSource].toString() : null,
              campaignId : row[ColumnIds.campaignID] ? row[ColumnIds.campaignID].toString() : null,
              productId : row[ColumnIds.productID] ? row[ColumnIds.productID].toString() : null,
              cLinkId : row[ColumnIds.cLinkId] ? row[ColumnIds.cLinkId].toString() : null,
              cLinkOwner : row[ColumnIds.cLinkOwner] ? row[ColumnIds.cLinkOwner].toString() : null,
              productsResponse : productsResponse,
              campaignsResponse : campaignsResponse,
              trafficSourceResponseDto : props.trafficSourceResponseDto,
              setTrafficSourceResponseDto : props.setTrafficSourceResponseDto,
              setProductsCampaignsDto : props.setProductsCampaignsDto
            };

            tdObj = <CategoricalCell 
                      key={tdKey} 
                      tdkey={tdKey} 
                      style={column.style} 
                      align={column.align} 
                      filteredColumn={column} 
                      cellValue={cellValue}  
                      formattedValue={formattedValue}
                      tooltipValue={tooltipFormattedValue}
                      page={props.page}
                      categoricalCellDto={categoricalCellDto}
                      currentUser={props.currentUser}
                    />;
          } else {
            tdObj = (
                <td key={tdKey} style={column.style} align={column.align} title={tooltipFormattedValue}>
                  {formattedValue}
                </td>
              );
          }
          tds.push(tdObj);
        }
      }
    });

    if (rowIndex >= initialIndex && rowIndex < lastIndex) {
      let trkey= "tr" + rowIndex.toString();
      trs.push(<tr key={trkey}>{tds}</tr>);
    }
  });

  // used for chart
  const showChar = pivotGroupBySelected && pivotGroupBySelected.id !== PivotIds.off;
  const yAxis = []
  const columnsForChart = [datagridColumns.get(ColumnIds.clicksReceived), datagridColumns.get(defaultColumnId)];
  if (showChar) {
    if (pivotFieldSelected === "All") {
      const firstDataset = rows.find((row) => datagridColumns.get(row["columnIdForFormat"]).id === ColumnIds.clicksReceived);
      const secondDataset = rows.find((row) => datagridColumns.get(row["columnIdForFormat"]).id === defaultColumnId);
      yAxis.push(firstDataset);
      yAxis.push(secondDataset);
    } else {
      const rowClicksReceived:any = [];
      const rowDefaultColumn:any = [];
      Object.keys(summaryRowForPivotByGroup).forEach((columnDate:any) => {
        if (summaryRowForPivotByGroup[columnDate][ColumnIds.clicksReceived]) {
          rowClicksReceived[columnDate] = summaryRowForPivotByGroup[columnDate][ColumnIds.clicksReceived];
        }
        if (summaryRowForPivotByGroup[columnDate][defaultColumnId]) {
          rowDefaultColumn[columnDate] = summaryRowForPivotByGroup[columnDate][defaultColumnId];
        }
      });
      yAxis.push(rowClicksReceived);
      yAxis.push(rowDefaultColumn);
      if (pivotFieldSelected !== ColumnIds.clicksReceived && pivotFieldSelected !== defaultColumnId) {
        columnsForChart.unshift(datagridColumns.get(pivotFieldSelected));
        if (isGroupingOnlyByDateOrMonth && rows.length === 1) {
          yAxis.unshift(rows[0]);
        } else {
          yAxis.unshift(summaryRow);
        }
      }
    }
  }

  return (
    <>
      <OtherFeaturesPanel page={props.page} 
                          setRefresh={setRefresh}
                          filteredColumns={filteredColumnsForExport}
                          dataLastUpdate={props.searchResponseDto.metadata.dataLastUpdate} 
                          pivotFieldSelected={pivotFieldSelected}
                          pivotGroupBySelected={pivotGroupBySelected}
                          onPivotChange={onPivotChange}
                          fields={fields}
                          rows={rows}/>
      <hr/>
      {
        showChar &&
        (
          <Row className="justify-content-md-center">
            <Col xl={10} className="text-center">
              <Chart xAxis={filteredColumns} yAxis={yAxis} columns={columnsForChart} />
            </Col>
          </Row>
        )
      }

      <Row >
        <Col xl={12} className="text-center">

          <Table style={{ fontSize: 11 }} striped bordered hover>
            <thead key={"thead"} style={{ position: "sticky", top: -2, background: "white" }}>
              <tr key={"th1"}>{columnHeaders}</tr>
            </thead>
            <tbody key={ sortState.columnIdSorted + "-" + sortState.sort }>
              { 
                mode !== PivotMode.PIVOT_ALL_FIELDS && <SummaryRow rowData={summaryRow} filteredColumns={filteredColumns} />
              }
              {trs}
            </tbody>
          </Table>
          <PaginationButtons size={rows.length} pageSize={tmpPageSize} actualPage={props.actualPage} 
            setActualPage={props.setActualPage} setPageSize={setPageSize}/>
        </Col>
      </Row>
    </>
  );
}